/**
 * CYBORG-989 — the client half of the relay-held workspace snapshot.
 *
 * `cyborg:bootstrap` answers `{ unchanged: true }` when the client already holds
 * the relay's current version, and that answer carries NO channels, members or
 * work sessions — which is the whole saving, and only works if the client can
 * produce the previous ones from somewhere. That somewhere is here: the same
 * IndexedDB the message scopes live in, keyed per viewer and workspace.
 *
 * Stored in APP_META (a plain key → JSON-string store) rather than a new object
 * store, because a new store is an IDB version bump and this payload is one small
 * row per workspace, read once on open. It is written ONLY from a successful
 * bootstrap that actually carried a snapshot, never optimistically and never from
 * an `unchanged` answer — Caveat #29, the same rule setMeta enforces for the
 * message cursors: a transient failure must not pin stale state.
 *
 * The workspace DRAIN cursor lives here too, for a different reason. `cyborg:sync`
 * is a WORKSPACE-wide read (`getMessagesSince(workspace, seq > lastSeq)`), so the
 * workspace-wide `seq` counter is exactly the right cursor for it — unlike the
 * per-scope `lastSyncedSeq` in meta.ts, which delta.ts documents at length as the
 * WRONG cursor for a per-conversation open. Persisting it stops a reload draining
 * the workspace from seq 0, which is up to 50 pages of 500 rows.
 */

import { getDB, reqDone, txDone, STORES, type AppMeta } from "./idb.js";
import { cacheEnabled } from "./enabled.js";
import { cacheReadable } from "./read-gate.js";
import { APP_META_USER_ID } from "./meta.js";
import { isViewerCacheKeySegment, viewerCacheKeySegment } from "./local-owner.js";

/** What the relay's snapshot half of a bootstrap answer carries, as stored. */
export interface StoredWorkspaceSnapshot {
  version: number;
  channels: unknown[];
  members: unknown[];
  external: unknown[];
  workSessions: unknown[];
}

const SNAPSHOT_PREFIX = "ws-snapshot:";
const DRAIN_SEQ_PREFIX = "ws-drain-seq:";

// Both rows hold one person's narrowed view (their private channels and sessions), so the key
// carries the viewer (CYBO-1846): `<prefix><viewer>:<workspaceId>`. No viewer: cache nothing.
function snapshotKey(prefix: string, workspaceId: string, viewer: string): string | undefined {
  return viewer ? `${prefix}${viewer}:${workspaceId}` : undefined;
}

/** Drop the rows older builds keyed by workspace alone (no viewer segment). */
export async function evictLegacySnapshots(): Promise<void> {
  const dbp = cacheEnabled() ? getDB() : undefined;
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.APP_META, "readwrite");
    const store = tx.objectStore(STORES.APP_META);
    const keys = (await reqDone(store.getAllKeys())) as IDBValidKey[];
    for (const key of keys) {
      const k = String(key);
      const prefix = [SNAPSHOT_PREFIX, DRAIN_SEQ_PREFIX].find((p) => k.startsWith(p));
      if (!prefix) continue;
      const [viewer = "", ...rest] = k.slice(prefix.length).split(":");
      if (!isViewerCacheKeySegment(viewer) || rest.length === 0) store.delete(key);
    }
    await txDone(tx);
  } catch {
    // intentional: best-effort sweep; a legacy key is never built, so it is never read.
  }
}

async function readAppMeta(key: string): Promise<string | null> {
  const dbp = cacheEnabled() ? getDB() : null;
  if (!dbp) return null;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.APP_META, "readonly");
    const row = (await reqDone(tx.objectStore(STORES.APP_META).get(key))) as AppMeta | undefined;
    return row?.value ?? null;
  } catch {
    return null;
  }
}

// A write is dropped unless this tab's viewer is still the device's stored owner: a second tab
// that signed in as someone else wiped and re-owned the cache, and this tab must not write into it.
// The owner read and the put share ONE readwrite transaction, so no claim can land between them.
// `accept` sees the row already held (monotonic cursors).
async function writeAppMetaIfOwner(
  viewer: string,
  key: string,
  value: string,
  accept: (held: string | undefined) => boolean = () => true,
): Promise<void> {
  const dbp = cacheEnabled() ? getDB() : null;
  if (!dbp || !viewer || !(await cacheReadable())) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.APP_META, "readwrite");
    const store = tx.objectStore(STORES.APP_META);
    const owner = (await reqDone(store.get(APP_META_USER_ID))) as AppMeta | undefined;
    const held = (await reqDone(store.get(key))) as AppMeta | undefined;
    if (viewerCacheKeySegment(owner?.value) === viewer && accept(held?.value)) {
      store.put({ key, value });
    }
    await txDone(tx);
  } catch {
    /* best-effort cache write; a miss just costs one full bootstrap */
  }
}

/**
 * The snapshot held for this workspace, or null when we hold none (first open,
 * a wiped cache, the cache switched off). Null means "send version 0", which the
 * relay can never match, so the answer always carries a full snapshot.
 */
export async function loadWorkspaceSnapshot(
  workspaceId: string,
  viewer: string,
): Promise<StoredWorkspaceSnapshot | null> {
  const key = snapshotKey(SNAPSHOT_PREFIX, workspaceId, viewer);
  if (!key || !(await cacheReadable())) return null; // null: a miss, this function's contract
  const raw = await readAppMeta(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredWorkspaceSnapshot;
    // A stored row with no version is unusable as a cursor — treat it as absent
    // rather than sending `version: undefined` and getting a coerced 0 anyway.
    return typeof parsed?.version === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export async function saveWorkspaceSnapshot(
  workspaceId: string,
  snapshot: StoredWorkspaceSnapshot,
  viewer: string,
): Promise<void> {
  const key = snapshotKey(SNAPSHOT_PREFIX, workspaceId, viewer);
  if (key) await writeAppMetaIfOwner(viewer, key, JSON.stringify(snapshot));
}

/** The highest workspace `seq` this client has drained, or 0 if none. */
export async function loadDrainSeq(workspaceId: string, viewer: string): Promise<number> {
  const key = snapshotKey(DRAIN_SEQ_PREFIX, workspaceId, viewer);
  if (!key || !(await cacheReadable())) return 0;
  const raw = await readAppMeta(key);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Monotonic: a lower cursor is ignored, so an out-of-order stamp can't rewind. */
export async function saveDrainSeq(
  workspaceId: string,
  seq: number,
  viewer: string,
): Promise<void> {
  const key = snapshotKey(DRAIN_SEQ_PREFIX, workspaceId, viewer);
  if (!key || !Number.isFinite(seq) || seq <= 0) return;
  await writeAppMetaIfOwner(viewer, key, String(seq), (held) => seq > Number(held ?? 0));
}
